<template>
  <ion-app>
    <ion-router-outlet />
    <data-refresh-pill />
    <balance-modal />
    <messages-controller />
  </ion-app>
</template>

<script setup lang="ts">
import { IonApp, IonRouterOutlet } from '@ionic/vue'
import { onMounted, watch } from 'vue'
import { useAuthStore } from './stores/auth'
import MessagesController from './components/MessagesController.vue'
import BalanceModal from './components/BalanceModal.vue'
import DataRefreshPill from './components/DataRefreshPill.vue'
import { useSettingsStore } from './stores/settingsStore'
import { useBackgroundRefresh } from './composables/useBackgroundRefresh'
import { useStoreInitialization } from './composables/useStoreInitialization'
import { usePostHog } from './composables/usePostHog'
import { useRecordAppFirstOpened } from './composables/useRecordAppFirstOpened'
import { useCampaignTracking } from './composables/useCampaignTracking'

const auth = useAuthStore()
const settings = useSettingsStore()
const { initializeStores } = useStoreInitialization()
const { init: initCampaignTracking } = useCampaignTracking()

useBackgroundRefresh(5) // Refresh Data if the app is in the background for 5 minutes
// Campaign init before first-open so cold-start launch URL is registered first
initCampaignTracking()
useRecordAppFirstOpened()

onMounted(() => {
  if (settings.darkMode === true) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
  auth.setInterceptor()
})

let initialized = false
let retryCount = 0
const MAX_RETRIES = 3

const initializeApp = async () => {
  if (initialized || retryCount >= MAX_RETRIES) return

  try {
    initialized = true
    auth.setAuthHeader()
    const { identifyExistingUser } = usePostHog()
    identifyExistingUser(auth.user)
    await initializeStores()
  } catch (error) {
    console.error(
      `Failed to initialize app (attempt ${retryCount + 1}/${MAX_RETRIES}):`,
      error,
    )
    initialized = false
    retryCount++

    if (retryCount < MAX_RETRIES) {
      setTimeout(() => {
        initializeApp()
      }, 1000)
    }
  }
}

watch(
  () => auth.stateLoaded && auth.user && auth.isLoggedIn,
  async (shouldInitialize) => {
    if (shouldInitialize) {
      await initializeApp()
    }
  },
  { immediate: true },
)

// Reset initialization flag when user logs out
watch(
  () => auth.isLoggedIn,
  (isLoggedIn) => {
    if (!isLoggedIn) {
      initialized = false
      retryCount = 0
    }
  },
)
</script>
